import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Tabs component for organizing content into separate panels
 * Supports multiple tabs with active state management, icons, and variants
 * Can be controlled (activeTab + onChange) or uncontrolled (defaultTab)
 *
 * `lazyMount` (default false): mount a tab's content only when the tab is first
 * activated, then keep it mounted (react-bootstrap `mountOnEnter` semantics).
 * Use for tabs with expensive content (e.g. many rich-text editors).
 */
const Tabs = ({
  tabs,
  activeTab,
  defaultTab = 0,
  variant = 'default',
  className = '',
  navClassName = '',
  onChange,
  contentClassName = '',
  lazyMount = false,
}) => {
  const [internalTab, setInternalTab] = useState(defaultTab);

  // Use activeTab if provided (controlled mode), otherwise use internal state
  const currentTab = activeTab !== undefined ? activeTab : internalTab;

  // Tabs that have been activated at least once (lazyMount mode)
  const mountedRef = useRef(new Set([currentTab]));
  mountedRef.current.add(currentTab);

  const handleTabClick = (index) => {
    if (activeTab === undefined) {
      // Uncontrolled mode
      setInternalTab(index);
    }
    onChange?.(index);
  };

  // Active/inactive classes are mutually exclusive per variant: conflicting
  // utilities (text color, bg, border color) must never coexist on the same
  // element — which class wins would depend on CSS source order, not on which
  // one was added last.
  const tabVariants = {
    // Segmented control — active tab lifts on white with shadow
    default: {
      navClass: 'tw-flex tw-flex-nowrap tw-items-center tw-gap-1 tw-bg-slate-100 tw-rounded-lg tw-p-1 tw-w-max',
      tabClass: 'tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-duration-150 tw-border-0 tw-select-none',
      inactiveClass: 'tw-text-slate-500 hover:tw-text-slate-700 tw-bg-transparent',
      activeClass: 'tw-bg-white tw-text-slate-800 tw-shadow-sm',
    },
    // Pill badges — colored active state
    pills: {
      navClass: 'tw-flex tw-flex-nowrap tw-items-center tw-gap-1.5 tw-p-1 tw-bg-slate-100 tw-rounded-lg tw-w-max',
      tabClass: 'tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded tw-cursor-pointer tw-transition-all tw-duration-150 tw-text-sm tw-font-medium tw-border-0 tw-select-none',
      inactiveClass: 'tw-text-slate-500 hover:tw-text-slate-700 tw-bg-transparent',
      activeClass: 'tw-bg-blue-600 tw-text-white tw-shadow-sm',
    },
    // Clean underline — 2 px bottom-border indicator
    // tw-border-solid is required: Tailwind preflight is disabled in this app,
    // so the browser's default button border-style is never reset.
    underline: {
      navClass: 'tw-flex tw-flex-nowrap tw-items-center tw-gap-1.5 tw-w-max tw-min-w-full tw-border-0 tw-border-b tw-border-solid tw-border-slate-200',
      tabClass: 'tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-duration-150 tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-b-2 tw-border-solid tw-select-none -tw-mb-px tw-rounded-t',
      inactiveClass: 'tw-text-slate-500 hover:tw-text-slate-800 tw-bg-transparent hover:tw-bg-slate-100 tw-border-b-transparent',
      activeClass: 'tw-text-blue-700 tw-font-semibold tw-bg-blue-50 tw-border-b-blue-600',
    },
  };

  const variant_config = tabVariants[variant];

  return (
    <div className={`tw-w-full ${className}`}>
      {/* Tab Navigation.

          The tablist is wrapped in its own horizontal scroller rather than
          being made scrollable itself. Tab labels are `whitespace-nowrap`, so a
          detail page with 6+ tabs is wider than a phone viewport; without this
          the strip stretched the page and dragged the whole layout sideways.

          A separate element is used because `overflow-x: auto` on the tablist
          would compute `overflow-y` to `auto` too, clipping the underline
          variant's `-mb-px` overlap and the active tab's bottom border. Keeping
          the scroller outside leaves the tablist's own box intact.

          `navClassName` still lands on the tablist, so the call sites that
          already pass their own `tw-overflow-x-auto ...` keep working — those
          utilities are simply redundant now. */}
      <div className="tw-w-full tw-overflow-x-auto tw-overscroll-x-contain tw-no-scrollbar">
        <div
          className={`${variant_config.navClass} ${navClassName}`}
          role="tablist"
        >
          {tabs.map((tab, index) => (
            <button
              key={index}
              // type="button" is critical: Tabs can render inside a <form>
              // (e.g. the PIF editor) where the default type="submit" would
              // submit the whole form on every tab click.
              type="button"
              onClick={() => handleTabClick(index)}
              role="tab"
              aria-selected={currentTab === index}
              aria-controls={`tab-panel-${index}`}
              // focus:outline-none — Tailwind preflight is disabled in this app, so
              // a clicked tab would otherwise keep the browser's default focus box
              // (a border-like ring). The active-tab styling already indicates it.
              className={`
                tw-outline-none focus:tw-outline-none focus-visible:tw-outline-none
                ${variant_config.tabClass}
                ${currentTab === index ? variant_config.activeClass : variant_config.inactiveClass}
              `}
            >
              {tab.icon && <span className="tw-flex-shrink-0">{tab.icon}</span>}
              <span className="tw-whitespace-nowrap">{tab.label}</span>
              {tab.badge !== undefined && tab.badge !== null && tab.badge !== '' && (
                <span
                  className={`tw-ml-1.5 tw-inline-flex tw-items-center tw-justify-center tw-min-w-[20px] tw-h-5 tw-px-1.5 tw-text-[11px] tw-font-bold tw-rounded-xl ${
                    tab.badgeColor
                      ? 'tw-text-white'
                      : currentTab === index
                        ? 'tw-bg-blue-100 tw-text-blue-700'
                        : 'tw-bg-slate-100 tw-text-slate-500'
                  }`}
                  style={tab.badgeColor ? { backgroundColor: tab.badgeColor } : undefined}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className={`${contentClassName}`}>
        {tabs.map((tab, index) => (
          <div
            key={index}
            id={`tab-panel-${index}`}
            role="tabpanel"
            aria-labelledby={`tab-${index}`}
            hidden={currentTab !== index}
            className={currentTab === index ? '' : 'tw-hidden'}
          >
            {lazyMount && !mountedRef.current.has(index) ? null : tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      badgeColor: PropTypes.string,
      content: PropTypes.node,
    })
  ).isRequired,
  activeTab: PropTypes.number,
  defaultTab: PropTypes.number,
  variant: PropTypes.oneOf(['default', 'pills', 'underline']),
  className: PropTypes.string,
  navClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  onChange: PropTypes.func,
  lazyMount: PropTypes.bool,
};

export default Tabs;
